import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  children: React.ReactNode;
  definition: string;
  term?: string;
}

const Tooltip: React.FC<TooltipProps> = ({ children, definition, term }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);

  const updatePosition = () => {
    if (triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = triggerRect.bottom + 8;
      let left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);

      // Adjust if tooltip goes off the right edge
      if (left + tooltipRect.width > viewportWidth - 20) {
        left = viewportWidth - tooltipRect.width - 20;
      }

      // Adjust if tooltip goes off the left edge
      if (left < 20) {
        left = 20;
      }

      // Show tooltip above if there's not enough space below
      if (top + tooltipRect.height > viewportHeight - 20) {
        top = triggerRect.top - tooltipRect.height - 8;
      }

      setPosition({ top, left });
    }
  };

  useEffect(() => {
    if (isVisible) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    }
  }, [isVisible]);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          color: '#18C3BA',
          borderBottom: '1px dotted #18C3BA',
          cursor: 'help',
          transition: 'all 0.2s ease',
          position: 'relative'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(24, 195, 186, 0.1)';
          e.currentTarget.style.borderBottomStyle = 'solid';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.borderBottomStyle = 'dotted';
        }}
      >
        {children}
      </span>

      {isVisible && createPortal(
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            background: 'rgba(10, 14, 20, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(24, 195, 186, 0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(24, 195, 186, 0.2)',
            zIndex: 9999,
            maxWidth: '280px',
            minWidth: '200px',
            fontSize: '0.9rem',
            lineHeight: '1.5',
            color: '#EFEFE3',
            fontFamily: "'Inter', 'Roboto', sans-serif",
            opacity: 0,
            transform: 'translateY(-5px) scale(0.95)',
            animation: 'tooltipFadeIn 0.2s ease-out forwards'
          }}
        >
          {term && (
            <div style={{
              color: '#18C3BA',
              fontWeight: 600,
              marginBottom: '6px',
              fontSize: '0.85rem',
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>
              {term}
            </div>
          )}
          <div style={{ color: '#D5D6CC' }}>
            {definition}
          </div>

          {/* Tooltip arrow */}
          <div style={{
            position: 'absolute',
            top: '-6px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: '6px solid rgba(24, 195, 186, 0.3)',
            filter: 'drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.1))'
          }} />
          <div style={{
            position: 'absolute',
            top: '-5px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: '5px solid rgba(10, 14, 20, 0.95)'
          }} />
        </div>,
        document.body
      )}

      <style>
        {`
          @keyframes tooltipFadeIn {
            from {
              opacity: 0;
              transform: translateY(-5px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
    </>
  );
};

export default Tooltip;